/**
 * `<html lang>` follows the UI language (WCAG 3.1.1). index.html ships
 * `lang="et"`, and before this only a manual pick in the language switcher
 * updated it — so the boot locale, the signed-in person's saved language
 * (AuthContext) and every other `changeLanguage` left screen readers
 * pronouncing Russian or Finnish text with Estonian rules.
 *
 * The one place that owns it: called once in main.tsx right after i18n is
 * created. It writes the current language immediately and again on every
 * `languageChanged`, whoever triggered it.
 */

interface LanguageSource {
  language?: string;
  resolvedLanguage?: string;
  on(event: 'languageChanged', cb: (lng: string) => void): void;
  off(event: 'languageChanged', cb: (lng: string) => void): void;
}

/** Primary subtag only: `en-US` → `en`. Nothing → `et`, as in index.html. */
export function langAttribute(lng: string | undefined): string {
  const primary = (lng ?? '').split(/[-_]/)[0]?.trim().toLowerCase();
  return primary && primary !== 'cimode' ? primary : 'et';
}

export function syncDocumentLang(
  i18n: LanguageSource,
  root: { setAttribute(name: string, value: string): void } = document.documentElement,
): () => void {
  const apply = (lng?: string) => root.setAttribute('lang', langAttribute(lng));
  apply(i18n.resolvedLanguage ?? i18n.language);
  const onChange = (lng: string) => apply(i18n.resolvedLanguage ?? lng);
  i18n.on('languageChanged', onChange);
  return () => i18n.off('languageChanged', onChange);
}
